import { useEffect, useState } from "react";
import { useDebouncedValue } from "./useDebouncedValue";

/** A word the backend matches on: a query with none asks nothing. */
const MIN_TERM_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

export interface StockListSearchState<T> {
  papers: T[];
  isLoading: boolean;
  /** The search failed: the field still takes the paper as typed. */
  isFailed: boolean;
}

/** Whether a query holds a word the stock list matches on. */
export function asksTheStockList(query: string): boolean {
  return query
    .trim()
    .split(/\s+/)
    .some((term) => term.length >= MIN_TERM_LENGTH);
}

/**
 * The stock list's papers for what is typed into a paper field's "Other
 * stock, quote on request" (manual RFQ slice 4), debounced, the latest query
 * winning. `search` is the adapter call for this field (staff: the field's
 * own URL; portal: the item and field) and must be stable (`useCallback`).
 * `enabled` false (the field offers no stock list, or the box is closed)
 * asks nothing.
 */
export function useStockListSearch<T>(
  query: string,
  search: (query: string) => Promise<T[]>,
  enabled: boolean,
): StockListSearchState<T> {
  const debounced = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [state, setState] = useState<StockListSearchState<T>>({
    papers: [],
    isLoading: false,
    isFailed: false,
  });
  const asks = enabled && asksTheStockList(debounced);

  useEffect(() => {
    if (!asks) {
      setState({ papers: [], isLoading: false, isFailed: false });
      return;
    }
    let isCurrent = true;
    setState((previous) => ({ ...previous, isLoading: true, isFailed: false }));
    search(debounced)
      .then((papers) => {
        if (isCurrent) setState({ papers, isLoading: false, isFailed: false });
      })
      .catch(() => {
        if (isCurrent)
          setState({ papers: [], isLoading: false, isFailed: true });
      });
    return () => {
      isCurrent = false;
    };
  }, [asks, debounced, search]);

  return state;
}
